/**
 * "Where is this image used?" — the list that makes delete-protection
 * understandable instead of just obstructive.
 *
 * Rows are grouped by the coarse bucket the API assigns (insurer / product /
 * website section / …) because that is the question a user actually has:
 * "will removing this break a page, or just a card?".
 */

import { Badge } from '@/components/common';
import { usageTypeLabel, type AssetUsage } from '@/services/assets';

interface AssetUsageListProps {
  usages: AssetUsage[];
  isLoading?: boolean;
  emptyMessage?: string;
}

export function AssetUsageList({ usages, isLoading, emptyMessage }: AssetUsageListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1].map((index) => (
          <div key={index} className="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700" />
        ))}
      </div>
    );
  }

  if (!usages.length) {
    return (
      <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:bg-gray-900/50 dark:text-gray-400">
        {emptyMessage || 'Nothing uses this image yet, so it is safe to delete.'}
      </p>
    );
  }

  const groups = new Map<string, AssetUsage[]>();
  usages.forEach((usage) => {
    const key = usage.usage_type || 'other';
    const bucket = groups.get(key) || [];
    bucket.push(usage);
    groups.set(key, bucket);
  });

  return (
    <div className="space-y-3">
      {Array.from(groups.entries()).map(([type, rows]) => (
        <div key={type}>
          <div className="mb-1.5 flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {usageTypeLabel(type)}
            </p>
            <Badge variant="default" size="sm">
              {rows.length}
            </Badge>
          </div>
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            {rows.map((usage) => (
              <li
                key={`${usage.object_type}-${usage.object_id}-${usage.field_name}`}
                className="flex items-center justify-between gap-3 bg-white px-3 py-2 text-sm dark:bg-gray-800"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900 dark:text-white">
                    {usage.object_label || usage.object_type}
                  </p>
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {usage.object_type}
                    {usage.field_name ? ` · ${usage.field_name.replace(/_/g, ' ')}` : ''}
                  </p>
                </div>
                {usage.broker_name && (
                  <Badge variant="info" size="sm">
                    {usage.broker_name}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
